import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import Reveal from "./Reveal";
import { api } from "@/lib/api";
import { insightsPlaceholders } from "@/data/site";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" }) : "";

function PlaceholderGrid({ testIdPrefix, limit }) {
  return (
    <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid={`${testIdPrefix}-placeholders`}>
      {insightsPlaceholders.slice(0, limit).map((a, i) => (
        <Reveal key={a.category} delay={(i % 3) * 0.1}>
          <article className="flex h-full flex-col rounded-xl border border-[#0B1F3A]/10 bg-white p-7" data-testid={`${testIdPrefix}-placeholder-${i}`}>
            <span className="self-start rounded-full bg-[#0B1F3A] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#C9A227]">{a.category}</span>
            <h3 className="mt-4 font-display text-lg font-bold leading-snug text-[#0B1F3A]">{a.title}</h3>
            <p className="mt-2 flex-1 text-sm leading-relaxed text-[#667085]">{a.summary}</p>
            <span className="mt-5 text-[12px] font-bold uppercase tracking-[0.18em] text-red-700">Coming soon</span>
          </article>
        </Reveal>
      ))}
    </div>
  );
}

export default function InsightsGrid({ testIdPrefix = "insight", limit = 3 }) {
  const [articles, setArticles] = useState(null);

  useEffect(() => {
    api.get("/insights").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  if (articles === null) {
    return <div className="mt-12 h-48 animate-pulse rounded-xl bg-[#F5F8FC]" data-testid={`${testIdPrefix}-loading`} />;
  }
  if (articles.length === 0) {
    return <PlaceholderGrid testIdPrefix={testIdPrefix} limit={limit} />;
  }

  return (
    <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid={`${testIdPrefix}-grid`}>
      {articles.slice(0, limit).map((a, i) => (
        <Reveal key={a.id} delay={(i % 3) * 0.1}>
          <Link
            to={`/insights/${a.slug}`}
            data-testid={`${testIdPrefix}-card-${a.slug}`}
            className="group flex h-full flex-col overflow-hidden rounded-xl border border-[#0B1F3A]/10 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#A87B1F]/40 hover:shadow-[0_20px_44px_-20px_rgba(21,101,192,0.35)]"
          >
            {a.image_url ? (
              <img src={a.image_url} alt={a.title} loading="lazy" className="aspect-[16/9] w-full object-cover" />
            ) : (
              <div className="flex aspect-[16/9] items-center justify-center bg-[#F5F8FC] text-[#A87B1F]/40">
                <BookOpen size={32} />
              </div>
            )}
            <div className="flex flex-1 flex-col p-7">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[#A87B1F]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#A87B1F]">{a.category}</span>
                <span className="text-[12px] text-[#98A2B3]">{fmtDate(a.published_at)}</span>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold leading-snug text-[#0B1F3A]">{a.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-[#667085]">{a.summary}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#A87B1F]">
                Read More
                <ArrowRight size={14} className="transition-transform duration-200 group-hover:translate-x-1" />
              </span>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  );
}
